import { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';

import type { Waypoint } from '@/types/waypoint';
import { WaypointType } from '@/types/waypoint';
import { fitMapToLocations } from '@/utils/map-utils';
import { useUserProfileStore } from '@/stores/user-profile-store';
import type { BlindMetadata, GameTrailMetadata } from '@/types/waypoint';
import type { Property } from '@/types/property';
import { TILE_LAYERS, TILE_LAYER_KEYS } from '@/services/map-tiles';
import type { TileLayerKey } from '@/services/map-tiles';
import { createMarkerElement, getMarkerAnchor } from '@/components/map/WaypointMarker';
import { MarkerInfoCard } from '@/components/map/MarkerInfoCard';
import { WindStreamletLayer } from '@/components/map/WindStreamletLayer';
import { BoundaryOverlay } from '@/components/map/BoundaryOverlay';
import { LayerControl } from '@/components/map/LayerControl';
import { useLayerControl } from '@/hooks/use-layer-control';
import { useWeatherStore } from '@/stores/weather-store';
import { HuntMap } from '@/components/map/HuntMap';
import { useMap } from '@/hooks/useMap';
import { FoodPlotBoundaryLayer } from '@/components/map/FoodPlotBoundaryLayer';
import {
  GAME_TRAIL_PAINT_BASE,
  GAME_TRAIL_DASHARRAY,
  GAME_TRAIL_DASH_FRAMES,
  GAME_TRAIL_DASH_FRAME_COUNT,
  GAME_TRAIL_FRAME_INTERVAL_MS,
  CLUSTER_SOURCE,
  CLUSTER_CIRCLE_LAYER,
  CLUSTER_COUNT_LAYER,
  resolveEmberColor,
} from '@/components/map/map-utils';
import { isWindDirectionFavorable } from '@/utils/wind-utils';
import { PublicLandLayer } from '@/components/map/PublicLandLayer';
import type { PublicLandFeatureProps } from '@/components/map/PublicLandLayer';
import { PublicLandInfoCard } from '@/components/map/PublicLandInfoCard';
import { UpsellSheet } from '@/components/common/UpsellSheet';
import { UPSELL_COPY } from '@/constants/upsell-copy';
import type { UpsellGateKey } from '@/constants/upsell-copy';

// ─── Zoom tier: only 4 discrete breakpoints to avoid constant marker rebuilds ──

function getZoomTier(z: number): 1 | 2 | 3 | 4 {
  if (z >= 18) return 4;
  if (z >= 15) return 3;
  if (z >= 13) return 2;
  return 1;
}

// ─── Cluster + individual location layer ─────────────────────────────────────

// ─── Game trail layer IDs ─────────────────────────────────────────────────────

const TRAIL_SOURCE_PREFIX = 'trail-src-';
const TRAIL_LINE_LAYER_PREFIX = 'trail-line-';

// ─── Game trail layer management ──────────────────────────────────────────────

function removeTrailLayers(map: maplibregl.Map, waypointIds: string[]) {
  for (const id of waypointIds) {
    const lineId = TRAIL_LINE_LAYER_PREFIX + id;
    const srcId = TRAIL_SOURCE_PREFIX + id;
    try {
      if (map.getLayer(lineId)) map.removeLayer(lineId);
      if (map.getSource(srcId)) map.removeSource(srcId);
    } catch { /* map may be destroyed */ }
  }
}

function addTrailLayers(map: maplibregl.Map, loc: Waypoint, pathCoords: [number, number][]) {
  const srcId = TRAIL_SOURCE_PREFIX + loc.id;
  const lineId = TRAIL_LINE_LAYER_PREFIX + loc.id;

  map.addSource(srcId, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: pathCoords },
          properties: {},
        },
      ],
    },
  });

  // Resolve the ember color from the live CSS custom property at mount time
  // (RULE-055: never hardcode hex). The color follows the active theme.
  const emberColor = resolveEmberColor();

  map.addLayer({
    id: lineId,
    type: 'line',
    source: srcId,
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: {
      'line-color': emberColor,
      ...GAME_TRAIL_PAINT_BASE,
    },
  });
}

/** Returns true when the user has reduced-motion enabled. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface LocationLayerProps {
  locations: Waypoint[];
  onMarkerClick: (location: Waypoint) => void;
  visible: boolean;
}

function LocationLayer({ locations, onMarkerClick, visible }: LocationLayerProps) {
  const map = useMap();
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const trailLocationIdsRef = useRef<string[]>([]);
  const zoomTierRef = useRef<number>(0);
  const mountedRef = useRef(true);
  const visibleRef = useRef(visible);
  // M-02: RAF handle for the trail dash animation; cleared on cleanup or
  // when reduced-motion is active.
  const trailAnimationFrameRef = useRef<number | null>(null);

  function stopTrailAnimation() {
    if (trailAnimationFrameRef.current !== null) {
      cancelAnimationFrame(trailAnimationFrameRef.current);
      trailAnimationFrameRef.current = null;
    }
  }

  /**
   * M-02: animate dash phase forward to suggest movement along each trail.
   * Skipped entirely when prefers-reduced-motion is active — in that case we
   * lock the static GAME_TRAIL_DASHARRAY (already applied during addLayer).
   *
   * Uses requestAnimationFrame with timestamp-throttling so frames advance at
   * GAME_TRAIL_FRAME_INTERVAL_MS cadence regardless of monitor refresh rate.
   */
  function startTrailAnimation() {
    stopTrailAnimation();
    if (prefersReducedMotion()) return;
    if (trailLocationIdsRef.current.length === 0) return;

    let frameIndex = 0;
    let lastFrameTs = 0;

    function step(ts: number) {
      if (!mountedRef.current) return;
      if (ts - lastFrameTs >= GAME_TRAIL_FRAME_INTERVAL_MS) {
        lastFrameTs = ts;
        frameIndex = (frameIndex + 1) % GAME_TRAIL_DASH_FRAME_COUNT;
        const nextDash = GAME_TRAIL_DASH_FRAMES[frameIndex] ?? GAME_TRAIL_DASHARRAY;
        try {
          for (const id of trailLocationIdsRef.current) {
            const lineId = TRAIL_LINE_LAYER_PREFIX + id;
            if (map.getLayer(lineId)) {
              map.setPaintProperty(lineId, 'line-dasharray', nextDash);
            }
          }
        } catch {
          /* map may be destroyed mid-frame — stop ourselves */
          stopTrailAnimation();
          return;
        }
      }
      trailAnimationFrameRef.current = requestAnimationFrame(step);
    }

    trailAnimationFrameRef.current = requestAnimationFrame(step);
  }

  function removeClusterLayers() {
    if (map.getLayer(CLUSTER_COUNT_LAYER)) map.removeLayer(CLUSTER_COUNT_LAYER);
    if (map.getLayer(CLUSTER_CIRCLE_LAYER)) map.removeLayer(CLUSTER_CIRCLE_LAYER);
    if (map.getSource(CLUSTER_SOURCE)) map.removeSource(CLUSTER_SOURCE);
  }

  function removeMarkers() {
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];
  }

  function addClusterLayers() {
    removeClusterLayers();

    // Exclude all game trails from cluster source (they render as lines; those without paths show nothing)
    const clusterLocations = locations.filter((loc) => loc.type !== WaypointType.GameTrail);

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: clusterLocations.map((loc) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [loc.longitude, loc.latitude] },
        properties: { id: loc.id },
      })),
    };

    map.addSource(CLUSTER_SOURCE, {
      type: 'geojson',
      data: geojson,
      cluster: true,
      clusterMaxZoom: 12,
      clusterRadius: 60,
    });

    map.addLayer({
      id: CLUSTER_CIRCLE_LAYER,
      type: 'circle',
      source: CLUSTER_SOURCE,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#302d29',
        'circle-radius': 17,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#524e48',
      },
    });

    map.addLayer({
      id: CLUSTER_COUNT_LAYER,
      type: 'symbol',
      source: CLUSTER_SOURCE,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['Open Sans Bold'],
        'text-size': 11,
      },
      paint: {
        'text-color': '#e8e5e0',
      },
    });
  }

  function rebuildMarkers(zoom: number) {
    removeMarkers();
    const tier = getZoomTier(zoom);
    if (tier === 1) return; // clusters only at low zoom

    for (const loc of locations) {
      // Game trails never show a pin — they render as polylines (or not at all if no path)
      if (loc.type === WaypointType.GameTrail) continue;

      const el = createMarkerElement(loc.type, zoom, loc.name);
      el.dataset.waypointId = loc.id;
      el.dataset.waypointType = loc.type;
      const marker = new maplibregl.Marker({ element: el, anchor: getMarkerAnchor(zoom) })
        .setLngLat([loc.longitude, loc.latitude])
        .addTo(map);
      marker.getElement().addEventListener('click', () => onMarkerClick(loc));
      markersRef.current.push(marker);
    }
  }

  function rebuildTrails() {
    stopTrailAnimation();
    removeTrailLayers(map, trailLocationIdsRef.current);
    trailLocationIdsRef.current = [];

    for (const loc of locations) {
      if (loc.type !== WaypointType.GameTrail) continue;
      const meta = loc.metadata as GameTrailMetadata | undefined;
      if (!meta?.pathGeoJSON) continue;
      // GeoJSONLineString coordinates are [lng, lat] — MapLibre expects [lng, lat]
      const pathCoords = meta.pathGeoJSON.coordinates as [number, number][];
      if (pathCoords.length < 2) continue;
      addTrailLayers(map, loc, pathCoords);
      trailLocationIdsRef.current.push(loc.id);
    }

    // M-02: start the dash animation only after at least one trail layer
    // is in the map. No-op when reduced-motion is active.
    startTrailAnimation();
  }

  function applyVisibility(v: boolean) {
    const vStr = v ? 'visible' : 'none';
    for (const m of markersRef.current) {
      (m.getElement() as HTMLElement).style.display = v ? '' : 'none';
    }
    try {
      if (map.getLayer(CLUSTER_CIRCLE_LAYER)) map.setLayoutProperty(CLUSTER_CIRCLE_LAYER, 'visibility', vStr);
      if (map.getLayer(CLUSTER_COUNT_LAYER)) map.setLayoutProperty(CLUSTER_COUNT_LAYER, 'visibility', vStr);
      for (const id of trailLocationIdsRef.current) {
        const lineId = TRAIL_LINE_LAYER_PREFIX + id;
        if (map.getLayer(lineId)) map.setLayoutProperty(lineId, 'visibility', vStr);
      }
    } catch { /* map may be destroyed */ }
  }

  function rebuild() {
    if (!mountedRef.current) return;
    const zoom = map.getZoom();
    const tier = getZoomTier(zoom);
    addClusterLayers();
    rebuildMarkers(zoom);
    rebuildTrails();
    applyVisibility(visibleRef.current);
    zoomTierRef.current = tier;
  }

  useEffect(() => {
    visibleRef.current = visible;
    applyVisibility(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    mountedRef.current = true;
    map.on('style.load', rebuild);
    if (map.isStyleLoaded()) rebuild();

    function onZoomEnd() {
      if (!mountedRef.current) return;
      const tier = getZoomTier(map.getZoom());
      if (tier !== zoomTierRef.current) {
        rebuildMarkers(map.getZoom());
        zoomTierRef.current = tier;
      } else {
        // Same tier but icons may need size update — refresh in-place
        removeMarkers();
        rebuildMarkers(map.getZoom());
      }
    }

    map.on('zoomend', onZoomEnd);

    return () => {
      mountedRef.current = false;
      map.off('style.load', rebuild);
      map.off('zoomend', onZoomEnd);
      stopTrailAnimation();
      try {
        removeClusterLayers();
        removeTrailLayers(map, trailLocationIdsRef.current);
      } catch { /* map may already be destroyed */ }
      removeMarkers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, onMarkerClick]);

  return null;
}

// ─── FitBoundsLayer ───────────────────────────────────────────────────────────

interface FitBoundsLayerProps {
  locations: Waypoint[];
  property: Property;
}

function FitBoundsLayer({ locations, property }: FitBoundsLayerProps) {
  const map = useMap();
  const hasInitializedRef = useRef(false);
  const defaultState = useUserProfileStore((s) => s.user?.defaultState ?? null);

  useEffect(() => {
    // Guard: the store may still hold the previous property's locations while
    // the new ones load. Only fit to locations that belong to this property.
    const propertyLocations = locations.filter((loc) => loc.propertyId === property.id);
    const animate = hasInitializedRef.current;
    hasInitializedRef.current = true;
    try {
      fitMapToLocations(map, { locations: propertyLocations, property, defaultState, animate });
    } catch { /* map may be destroyed */ }
  }, [map, locations, property, defaultState]);

  return null;
}

// ─── PropertyMap ─────────────────────────────────────────────────────────────

interface PropertyMapProps {
  property: Property;
  locations: Waypoint[];
  onMapReady?: (map: maplibregl.Map) => void;
  /** Initial tile layer. Defaults to the first key ('default'). */
  defaultTileLayer?: TileLayerKey;
  /** Overlay toggle keys to hide from LayerControl. E.g. ['boundary', 'windCones']. */
  hiddenOverlays?: string[];
  /**
   * When true, map starts with interactions disabled. User must tap to activate.
   * Use for maps embedded in scrollable pages (e.g. PropertyDetailPage).
   */
  requireTapToInteract?: boolean;
}

export function PropertyMap({ property, locations, onMapReady, defaultTileLayer, hiddenOverlays, requireTapToInteract }: PropertyMapProps) {
  const center: [number, number] = [property.latitude, property.longitude];
  const [activeStyle, setActiveStyle] = useState<TileLayerKey>(defaultTileLayer ?? TILE_LAYER_KEYS[0]);
  const [selectedLocation, setSelectedLocation] = useState<Waypoint | null>(null);
  const [selectedPublicLand, setSelectedPublicLand] = useState<PublicLandFeatureProps | null>(null);
  const [lockedUpsellGate, setLockedUpsellGate] = useState<UpsellGateKey | null>(null);
  const [layerPanelOpen, setLayerPanelOpen] = useState(false);
  const mapRef = useRef<maplibregl.Map | null>(null);
  // mapInstance state mirrors mapRef so effects that need the map instance can depend on it.
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null);

  const hiddenSet = new Set(hiddenOverlays ?? []);
  const { overlays, setShowWind, setShowBoundary, setShowLocations, setShowPublicLand } = useLayerControl();

  const { getConditions, fetchConditions } = useWeatherStore();
  const conditions = getConditions(property.id);

  useEffect(() => {
    if (!conditions) {
      void fetchConditions(property.id, property.latitude, property.longitude);
    }
  }, [property.id, property.latitude, property.longitude, conditions, fetchConditions]);

  // Style switching via map instance
  useEffect(() => {
    if (mapRef.current) {
      try { mapRef.current.setStyle(TILE_LAYERS[activeStyle].style); } catch { /* map may be destroyed */ }
    }
  }, [activeStyle]);

  const isPro = useUserProfileStore((s) => s.isPro);
  const publicLandConfigured = !!import.meta.env.VITE_PUBLIC_LAND_PMTILES_URL;
  const isPublicLandLocked = !isPro();
  const showPublicLandLayer = isPro() && publicLandConfigured;

  function handleLockedTap(featureName: string) {
    if (featureName === 'Public Land') {
      setLockedUpsellGate('public_land_overlay');
    }
  }

  const handleCanvasDismiss = useCallback(() => {
    setLayerPanelOpen(false);
  }, []);

  // Dismiss the layer panel when the user taps the raw map canvas.
  // Marker clicks are DOM events that do NOT propagate to map.on('click') (RULE-104),
  // so this only fires on bare-canvas taps — exactly the desired behaviour.
  // Depends on mapInstance (not mapRef) so the effect re-runs after onMapReady fires.
  useEffect(() => {
    if (!mapInstance) return;
    mapInstance.on('click', handleCanvasDismiss);
    return () => {
      mapInstance.off('click', handleCanvasDismiss);
    };
  }, [mapInstance, handleCanvasDismiss]);

  // ── Favorability dots on blind markers ────────────────────────────────────
  // Visible only when the wind overlay is active. Adds/removes a small
  // 6px dot at the top-right of each Blind marker element with data-favorability attribute.

  const showFavorabilityDots = overlays.showWind;

  useEffect(() => {
    const container = mapRef.current?.getCanvasContainer();
    if (!container) return;

    const blindEls = container.querySelectorAll<HTMLElement>('[data-waypoint-type="blind"]');

    if (!showFavorabilityDots) {
      // Remove all dots when overlay is off
      blindEls.forEach((el) => {
        const dot = el.querySelector('[data-favorability]');
        if (dot) dot.remove();
      });
      return;
    }

    blindEls.forEach((el) => {
      const id = el.dataset.waypointId;
      if (!id) return;
      const loc = locations.find((l) => l.id === id);
      if (!loc || loc.type !== WaypointType.Blind) return;

      const meta = loc.metadata as BlindMetadata | undefined;
      const idealDirs = meta?.idealWindDirections ?? [];

      // Determine favorability
      let isFavorable: boolean | null = null;
      if (conditions?.windDirection != null && idealDirs.length > 0) {
        // Convert cardinal back to degrees to use isWindDirectionFavorable
        isFavorable = conditions?.windDeg != null
          ? isWindDirectionFavorable(conditions.windDeg, idealDirs)
          : false;
      }

      // Remove old dot before re-adding
      const existingDot = el.querySelector('[data-favorability]');
      if (existingDot) existingDot.remove();

      if (isFavorable === null) return;

      const dot = document.createElement('div');
      dot.setAttribute('data-favorability', isFavorable ? 'favorable' : 'unfavorable');
      dot.style.cssText = [
        'position:absolute',
        'top:-3px',
        'right:-3px',
        'width:6px',
        'height:6px',
        'border-radius:50%',
        'border:1.5px solid rgba(255,255,255,0.85)',
        'pointer-events:none',
        'z-index:10',
      ].join(';');
      dot.style.background = isFavorable ? 'var(--color-favorable)' : 'var(--color-accent)';
      el.style.position = 'relative';
      el.appendChild(dot);
    });
  }, [showFavorabilityDots, conditions, locations]);

  function handleMarkerClick(location: Waypoint) {
    setSelectedPublicLand(null);
    setSelectedLocation(location);
    setLayerPanelOpen(false);
  }

  function handlePublicLandClick(props: PublicLandFeatureProps) {
    setSelectedLocation(null);
    setSelectedPublicLand(props);
    setLayerPanelOpen(false);
  }

  return (
    <div className="relative h-full w-full">
      <HuntMap
        center={center}
        zoom={14}
        className="h-full w-full"
        onMapReady={(map) => { mapRef.current = map; setMapInstance(map); onMapReady?.(map); }}
        initialStyle={activeStyle}
        requireTapToInteract={requireTapToInteract}
      >
        <FitBoundsLayer locations={locations} property={property} />
        <LocationLayer
          locations={locations}
          onMarkerClick={handleMarkerClick}
          visible={overlays.showLocations}
        />
        {!hiddenSet.has('wind') && (
          <WindStreamletLayer
            windDeg={conditions?.windDeg ?? null}
            windSpeedMph={conditions?.windSpeed ?? null}
            visible={overlays.showWind}
            locations={locations}
            currentWindCardinal={conditions?.windDirection ?? null}
          />
        )}
        {!hiddenSet.has('boundary') && (
          <BoundaryOverlay
            boundary={property.boundary}
            visible={overlays.showBoundary}
          />
        )}
        <FoodPlotBoundaryLayer
          locations={locations}
          visible={overlays.showLocations}
        />
        {showPublicLandLayer && (
          <PublicLandLayer
            visible={overlays.showPublicLand}
            onFeatureClick={handlePublicLandClick}
          />
        )}
      </HuntMap>

      <LayerControl
        activeTile={activeStyle}
        onTileChange={setActiveStyle}
        showWind={overlays.showWind}
        onWindChange={setShowWind}
        hasWeatherData={conditions !== null}
        showBoundary={overlays.showBoundary}
        onBoundaryChange={setShowBoundary}
        hasBoundary={!!property.boundary && property.boundary.length >= 3}
        showLocations={overlays.showLocations}
        onLocationsChange={setShowLocations}
        showPublicLand={overlays.showPublicLand}
        onPublicLandChange={setShowPublicLand}
        isPublicLandLocked={isPublicLandLocked}
        onLockedTap={handleLockedTap}
        hiddenOverlays={hiddenOverlays}
        isOpen={layerPanelOpen}
        onOpenChange={setLayerPanelOpen}
      />

      {selectedLocation && (
        <MarkerInfoCard
          location={selectedLocation}
          propertyId={property.id}
          onDismiss={() => setSelectedLocation(null)}
          windOverlayActive={overlays.showWind}
          currentWindCardinal={conditions?.windDirection}
          currentWindSpeed={conditions?.windSpeed}
        />
      )}

      {selectedPublicLand && (
        <PublicLandInfoCard
          designation={selectedPublicLand.designation}
          name={selectedPublicLand.name}
          manager={selectedPublicLand.manager}
          acres={selectedPublicLand.acres}
          onDismiss={() => setSelectedPublicLand(null)}
        />
      )}

      {lockedUpsellGate && (
        <UpsellSheet
          featureName={UPSELL_COPY[lockedUpsellGate].featureName}
          description={UPSELL_COPY[lockedUpsellGate].description}
          featureGate={lockedUpsellGate}
          onDismiss={() => setLockedUpsellGate(null)}
        />
      )}
    </div>
  );
}
